// src/components/Charts/Charts.js - Corrigido para suportar múltiplos perfis
import React, { useState, useEffect, useCallback, useRef } from "react";
import { auth, db, storage } from "../../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from "firebase/firestore";
import { ref, getBlob } from "firebase/storage";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell
} from "recharts";
import MainLayout from "../Layout/MainLayout";
import "./Charts.css";
import { useUserContext } from "../../contexts/UserContext";

// Importar serviços refatorados para garantir consistência
import { 
  loadAvailablePeriods,
  loadCategoryMappings,
  loadTransactionsForPeriod
} from "../../services/transactionRepository";

import { checkTransactionCategorization } from "../../services/categorizationService";

const Charts = () => {
  // Obter o contexto completo do usuário incluindo perfis ativos
  const { 
    activeAccountType, 
    accountSwitchTrigger,
    getAccountSubpath,
    activeBusinessProfileId,
    businessProfiles 
  } = useUserContext();
  
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [allAvailablePeriods, setAllAvailablePeriods] = useState([]);
  const [financialData, setFinancialData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [error, setError] = useState("");

  const [selectedMetric, setSelectedMetric] = useState("resultadoFinal");
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filterByCategory, setFilterByCategory] = useState(false);
  const [activeChart, setActiveChart] = useState("bar");

  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingBackground, setLoadingBackground] = useState(false);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const backgroundLoadTriggered = useRef(false);
  const [periodsToShow, setPeriodsToShow] = useState(6);

  const periodOptions = [
    { value: 3, label: "3 meses" },
    { value: 6, label: "6 meses" },
    { value: 12, label: "12 meses" },
    { value: 24, label: "24 meses" },
    { value: 0, label: "Todos" }
  ];

  const COLORS = [
    '#3273dc', '#20c997', '#8e44ad', '#f39c12',
    '#e74c3c', '#1abc9c', '#7d5fff', '#27ae60',
    '#e84393', '#2980b9'
  ];

  const metrics = [
    { id: "custosDiretos", name: "Custos Diretos" },
    { id: "lucroBruto", name: "Lucro Bruto" },
    { id: "resultadoFinal", name: "Resultado Final" },
  ];

  // Obter informações do perfil ativo para exibição
  const getActiveProfileInfo = () => {
    if (activeAccountType === 'personal') {
      return { name: 'Pessoal', type: 'personal' };
    } else {
      const activeProfile = businessProfiles.find(p => p.id === activeBusinessProfileId);
      return { 
        name: activeProfile?.profileName || 'Empresarial', 
        type: 'business',
        profileId: activeBusinessProfileId 
      };
    }
  };

  const activeProfileInfo = getActiveProfileInfo();

  // Função calculateFinancialDataForPeriod - Atualizada para usar múltiplos perfis
  const calculateFinancialDataForPeriod = useCallback(async (period, periodLabel) => {
    try {
      // Inicializar valores financeiros
      const financialTotals = {
        totalTransactions: 0,
        receitaBruta: 0,
        custosDiretos: 0,
        despesasOperacionais: 0,
        outrasReceitas: 0,
        despesasSocios: 0,
        investimentos: 0,
        naoCategorizado: 0,
        deducoesReceita: 0
      };

      // Para armazenar dados detalhados por categoria
      const categoryDetails = {};

      // Usar o serviço refatorado para carregar mapeamentos
      const categoryMappings = await loadCategoryMappings();

      // Identificar mapeamentos específicos por ID de transação
      const specificMappings = {};
      Object.entries(categoryMappings).forEach(([key, mapping]) => {
        if (mapping.isSpecificMapping && mapping.transactionId) {
          specificMappings[mapping.transactionId] = mapping;
        }
      });

      // Helper function to process transaction by category
      const processTransaction = (transaction, isCashEntry = false) => {
          financialTotals.totalTransactions += transaction.amount;
          let mainCategory = null;
          let subCategory = null;
          let categoryPath = "";
          let isCategorized = false;

          if (!isCashEntry && specificMappings[transaction.id]) {
              const specificMapping = specificMappings[transaction.id];
              mainCategory = specificMapping.groupName;
              subCategory = specificMapping.categoryName;
              categoryPath = specificMapping.categoryPath || "";
              isCategorized = true;
          } else if (isCashEntry && transaction.categoryPath) {
              const pathParts = transaction.categoryPath.split('.');
              if (pathParts.length >= 2) {
                  mainCategory = pathParts[0];
                  subCategory = pathParts[1];
                  categoryPath = transaction.categoryPath;
                  isCategorized = true;
              }
          } else if (!isCashEntry) {
               // Usar o serviço de categorização
              const result = checkTransactionCategorization(transaction, categoryMappings);
              if (result.isCategorized) {
                  mainCategory = result.mainCategory;
                  subCategory = result.subCategory;
                  categoryPath = result.categoryPath;
                  isCategorized = true;
              }
          }

          // Categorizar com base no grupo principal
          if (isCategorized) {
              if (mainCategory === "RECEITA" || mainCategory === "💸 Receitas") {
                  financialTotals.receitaBruta += transaction.amount;
              } else if (mainCategory === "(-) CUSTOS DAS MERCADORIAS VENDIDAS (CMV)" || 
                        mainCategory === "(-) CUSTOS DOS SERVIÇOS PRESTADOS (CSP)") {
                  financialTotals.custosDiretos += transaction.amount;
              } else if (mainCategory === "(-) DESPESAS OPERACIONAIS" || mainCategory === "🧾 Despesas Fixas" ||
                        mainCategory === "🍞 Despesas Variáveis") {
                  financialTotals.despesasOperacionais += transaction.amount;
              } else if (mainCategory === "(-) DEDUÇÕES DA RECEITA") {
                  financialTotals.deducoesReceita += transaction.amount;
              } else if (mainCategory === "(+) OUTRAS RECEITAS OPERACIONAIS E NÃO OPERACIONAIS") {
                  financialTotals.outrasReceitas += transaction.amount;
              } else if (mainCategory === "(-) DESPESAS COM SÓCIOS") {
                  financialTotals.despesasSocios += transaction.amount;
              } else if (mainCategory === "(-) INVESTIMENTOS" || mainCategory === "💳 Pagamentos e Transferências") {
                  financialTotals.investimentos += transaction.amount;
              }

              // Armazenar detalhes por categoria
              if (categoryPath && subCategory) {
                  if (!categoryDetails[categoryPath]) {
                      categoryDetails[categoryPath] = {
                          group: mainCategory,
                          category: subCategory,
                          value: 0
                      };
                  }
                  categoryDetails[categoryPath].value += transaction.amount;
              }

          } else {
              financialTotals.naoCategorizado += transaction.amount;
          }
      };

      // 1. Usar o serviço refatorado para obter transações
      const { transactions: allTransactions } = await loadTransactionsForPeriod(period);

      for (const transaction of allTransactions) {
        processTransaction(transaction, false);
      }

      // 2. Processar entradas de dinheiro usando o subcaminho correto
      const accountSubpath = getAccountSubpath();
      
      const cashEntriesQuery = query(
        collection(db, "cashEntries", auth.currentUser.uid, accountSubpath),
        where("period", "==", period)
      );
      const cashEntriesSnapshot = await getDocs(cashEntriesQuery);
      cashEntriesSnapshot.forEach(doc => {
        const entry = doc.data();
        if (entry.categoryPath && typeof entry.amount === 'number') {
          processTransaction(entry, true);
        }
      });

      // Calcular valores derivados
      const totalWithoutOutrasReceitas = financialTotals.receitaBruta +
                                        financialTotals.custosDiretos +
                                        financialTotals.despesasOperacionais +
                                        financialTotals.deducoesReceita +
                                        financialTotals.despesasSocios +
                                        financialTotals.investimentos +
                                        financialTotals.naoCategorizado;

      const lucroBruto = financialTotals.receitaBruta + financialTotals.custosDiretos;
      const resultadoFinal = totalWithoutOutrasReceitas;

      return {
        periodSummary: {
          period,
          periodLabel,
          receitaBruta: financialTotals.receitaBruta,
          custosDiretos: financialTotals.custosDiretos,
          lucroBruto,
          despesasOperacionais: financialTotals.despesasOperacionais,
          outrasReceitas: financialTotals.outrasReceitas,
          resultadoFinal,
          custosDiretosAbs: Math.abs(financialTotals.custosDiretos),
        },
        periodCategories: {
          period,
          periodLabel,
          categories: categoryDetails
        }
      };

    } catch (error) {
      console.error(`Erro ao calcular dados para o período ${period}:`, error);
      return {
        periodSummary: { period, periodLabel, receitaBruta: 0, custosDiretos: 0, lucroBruto: 0, resultadoFinal: 0, custosDiretosAbs: 0 },
        periodCategories: { period, periodLabel, categories: {} }
      };
    }
  }, [getAccountSubpath]); // Adicionar getAccountSubpath como dependência

  // Função loadDataForPeriods
  const loadDataForPeriods = useCallback(async (periodsArray) => {
    const summaryResults = [];
    const categoryResults = [];

    for (const period of periodsArray) {
      const { periodSummary, periodCategories } = await calculateFinancialDataForPeriod(period.value, period.label);
      summaryResults.push(periodSummary);
      categoryResults.push(periodCategories);
    }

    return { summaryResults, categoryResults };
  }, [calculateFinancialDataForPeriod]);

  // Função loadCategories - Atualizada para usar múltiplos perfis
  const loadCategories = useCallback(async () => {
    try {
      if (!auth.currentUser) return;

      const accountSubpath = getAccountSubpath();
      const isPersonal = activeAccountType === 'personal';
      
      let userCategoriesDocRef;
      
      if (isPersonal) {
        // Para conta pessoal, usar a subcoleção "personal/categories"
        userCategoriesDocRef = doc(db, "userCategories", auth.currentUser.uid, "personal", "categories");
      } else {
        // Para perfil empresarial, usar a lógica baseada no perfil ativo
        if (accountSubpath === 'business') {
          // Para o perfil principal, verificar primeiro no novo caminho estruturado
          userCategoriesDocRef = doc(db, "userCategories", auth.currentUser.uid, "business", "categories");
          
          // Verificar se o documento existe
          const docSnapshot = await getDoc(userCategoriesDocRef);
          
          // Se não existir, tentar o caminho legado (retrocompatibilidade)
          if (!docSnapshot.exists()) {
            console.log(`Documento de categorias não encontrado para perfil business, tentando caminho antigo...`);
            userCategoriesDocRef = doc(db, "userCategories", auth.currentUser.uid);
          }
        } else {
          // Para outros perfis empresariais, usar o ID do perfil
          userCategoriesDocRef = doc(db, "userCategories", auth.currentUser.uid, accountSubpath, "categories");
        }
      }

      const userCategoriesDoc = await getDoc(userCategoriesDocRef);

      if (userCategoriesDoc.exists()) {
        const categoriesData = userCategoriesDoc.data();

        // Processar categorias com base no tipo de conta
        if (activeAccountType === 'personal') {
          // Para conta pessoal, incluir todas as categorias
          const categoriesList = [];
          const categoriesObj = categoriesData.categories || {};

          Object.keys(categoriesObj).forEach(path => {
            if (categoriesObj[path]) {
              const parts = path.split('.');
              if (parts.length === 2) {
                const group = parts[0];
                const category = parts[1];

                categoriesList.push({
                  id: path,
                  group: group,
                  name: category,
                  displayName: `${group} - ${category}`
                });
              }
            }
          });

          // Ordenar alfabeticamente
          categoriesList.sort((a, b) => a.name.localeCompare(b.name));
          setAvailableCategories(categoriesList);
        }
        else {
          // Para conta empresarial, excluir categorias do grupo "RECEITA"
          if (categoriesData.categories) {
            const categoriesList = [];

            Object.keys(categoriesData.categories).forEach(path => {
              if (categoriesData.categories[path]) {
                const parts = path.split('.');
                if (parts.length === 2) {
                  const group = parts[0];
                  const category = parts[1];

                  // Ignorar categorias do grupo "RECEITA"
                  if (group !== "RECEITA") {
                    categoriesList.push({
                      id: path,
                      group: group,
                      name: category,
                      displayName: `${group} - ${category}`
                    });
                  }
                }
              }
            });

            // Ordenar categorias alfabeticamente
            categoriesList.sort((a, b) => {
              if (a.group !== b.group) return a.group.localeCompare(b.group);
              return a.name.localeCompare(b.name);
            });

            setAvailableCategories(categoriesList);
          }
        }
      }
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  }, [activeAccountType, getAccountSubpath]); // Adicionar dependências corretas

  // Carregar dados restantes em background
  const loadRemainingChartDataInBackground = useCallback(async (allPeriods) => {
    if (loadingBackground || allDataLoaded || backgroundLoadTriggered.current) {
      console.log("Background load skipped.");
      return;
    }

    backgroundLoadTriggered.current = true;
    setLoadingBackground(true);
    console.log("Iniciando carregamento em background (períodos mais antigos)...");

    try {
      const remainingPeriods = allPeriods.slice(0, Math.max(0, allPeriods.length - 6));

      if (remainingPeriods.length === 0) {
        setAllDataLoaded(true);
        setLoadingBackground(false);
        console.log("Não há mais períodos antigos para carregar em background.");
        return;
      }

      console.log(`Carregando ${remainingPeriods.length} períodos mais antigos em background...`);
      const { summaryResults, categoryResults } = await loadDataForPeriods(remainingPeriods);

      setFinancialData(prevData => [...prevData, ...summaryResults]);
      setCategoryData(prevData => [...prevData, ...categoryResults]);
      setAllDataLoaded(true);
      console.log("Carregamento em background (períodos antigos) concluído.");

    } catch (error) {
      console.error("Erro ao carregar dados antigos em background:", error);
      setError("Erro ao carregar dados adicionais.");
    } finally {
      setLoadingBackground(false);
    }
  }, [loadDataForPeriods, loadingBackground, allDataLoaded]);

  // Efeito principal para carregar dados iniciais
  useEffect(() => {
    const initializeCharts = async () => {
      setLoading(true);
      setAllDataLoaded(false);
      backgroundLoadTriggered.current = false;
      setFinancialData([]);
      setCategoryData([]);
      setSelectedCategories([]);

      if (auth.currentUser) {
        setUser(auth.currentUser);
        await loadCategories();

        try {
          // Usar o serviço refatorado para carregar períodos
          const availablePeriods = await loadAvailablePeriods();
          setAllAvailablePeriods(availablePeriods);

          if (availablePeriods.length > 0) {
            setLoadingInitial(true);

            // Pegar os 6 períodos mais recentes
            const initialPeriodsToLoad = availablePeriods.slice(-6);
            console.log(`Carregando ${initialPeriodsToLoad.length} períodos iniciais (mais recentes)...`);

            const { summaryResults, categoryResults } = await loadDataForPeriods(initialPeriodsToLoad);

            setFinancialData(summaryResults);
            setCategoryData(categoryResults);
            setLoadingInitial(false);
            setLoading(false);
            console.log("Carregamento inicial (mais recentes) concluído.");

            // Carregar períodos mais antigos em background se houver
            if (availablePeriods.length > 6) {
               setTimeout(() => loadRemainingChartDataInBackground(availablePeriods), 100);
            } else {
               setAllDataLoaded(true);
               console.log("Todos os períodos disponíveis foram carregados inicialmente.");
            }

          } else {
            setLoading(false);
            setAllDataLoaded(true);
          }
        } catch (error) {
          console.error("Erro ao inicializar gráficos:", error);
          setError("Não foi possível carregar os dados dos períodos.");
          setLoadingInitial(false);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    initializeCharts();
  }, [loadCategories, loadDataForPeriods, activeAccountType, activeBusinessProfileId, accountSwitchTrigger]); // Incluir activeBusinessProfileId

  // Função handleCategorySelection
  const handleCategorySelection = (categoryId) => {
     setSelectedCategories(prevSelected => {
      if (prevSelected.includes(categoryId)) {
        return prevSelected.filter(id => id !== categoryId);
      } else {
        return [...prevSelected, categoryId];
      }
    });
  };

  // Função handlePeriodsToShowChange
  const handlePeriodsToShowChange = (event) => {
    const value = parseInt(event.target.value);
    setPeriodsToShow(value);
    event.target.blur();
  };

  // Função formatPeriod
  const formatPeriod = (period) => {
    const foundPeriod = allAvailablePeriods.find(p => p.value === period);
    return foundPeriod ? foundPeriod.label : period;
  };

  // Função formatCurrency
  const formatCurrency = (value) => {
     return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Função prepareChartData
  const prepareChartData = () => {
    const sortedFinancialData = [...financialData].sort((a, b) => b.period.localeCompare(a.period));
    const sortedCategoryData = [...categoryData].sort((a, b) => b.period.localeCompare(a.period));

    const count = periodsToShow === 0 ? sortedFinancialData.length : periodsToShow;
    const dataToUse = sortedFinancialData.slice(0, count);
    const categoryDataToUse = sortedCategoryData.slice(0, count);

    dataToUse.reverse();
    categoryDataToUse.reverse();

    if (!filterByCategory || selectedCategories.length === 0) {
      return dataToUse.map(period => {
        const metricValue = selectedMetric === 'custosDiretos' ?
                          (period['custosDiretosAbs'] !== undefined ? period['custosDiretosAbs'] : Math.abs(period[selectedMetric] || 0)) :
                          period[selectedMetric];
        return {
          name: formatPeriod(period.period),
          value: metricValue || 0
        };
      });
    } else {
      const multiCategoryData = dataToUse.map((period, index) => {
        const result = { name: formatPeriod(period.period) };
        const currentCategoryData = categoryDataToUse[index]?.categories || {};
        selectedCategories.forEach(categoryId => {
          const category = availableCategories.find(c => c.id === categoryId);
          const categoryName = category ? category.name : categoryId.split('.')[1] || 'Desconhecida';
          result[categoryName] = currentCategoryData[categoryId]?.value || 0;
        });
        return result;
      });
      return multiCategoryData;
    }
  };

  // Função prepareSummaryTableData
  const prepareSummaryTableData = () => {
    const sortedFinancialData = [...financialData].sort((a, b) => b.period.localeCompare(a.period));
    const sortedCategoryData = [...categoryData].sort((a, b) => b.period.localeCompare(a.period));

    const count = periodsToShow === 0 ? sortedFinancialData.length : periodsToShow;
    const dataToUse = sortedFinancialData.slice(0, count);
    const categoryDataToUse = sortedCategoryData.slice(0, count);

     dataToUse.reverse();
     categoryDataToUse.reverse();

    let results = [];
    if (!filterByCategory || selectedCategories.length === 0) {
        results = dataToUse.map(period => ({
            ...period,
            custosDiretosAbs: period.custosDiretosAbs !== undefined ? period.custosDiretosAbs : Math.abs(period.custosDiretos || 0)
        }));
    } else {
        results = dataToUse.map((period, index) => {
            const result = {
                ...period,
                custosDiretosAbs: period.custosDiretosAbs !== undefined ? period.custosDiretosAbs : Math.abs(period.custosDiretos || 0)
            };
            const categoriesForPeriod = categoryDataToUse[index]?.categories || {};
            selectedCategories.forEach(categoryId => {
                const category = availableCategories.find(c => c.id === categoryId);
                const categoryName = category ? category.name : categoryId.split('.')[1] || 'Desconhecida';
                result[categoryName] = categoriesForPeriod[categoryId]?.value || 0;
            });
            return result;
        });
    }
    return results;
  };

  // Função renderLineChart
  const renderLineChart = () => {
    const data = prepareChartData();
    const metricName = filterByCategory && selectedCategories.length > 0
      ? "Categorias Selecionadas"
      : (metrics.find(m => m.id === selectedMetric)?.name || selectedMetric);

    const chartTitle = filterByCategory && selectedCategories.length > 0
      ? "Evolução de Categorias por Período"
      : `Evolução de ${metricName} por Período`;

    return (
      <div className="chart-container enhanced-chart">
        <div className="chart-header">
          <h3>
            {chartTitle}
            {filterByCategory && selectedCategories.length > 0 && (
              <span className="selected-categories-count">{selectedCategories.length}</span>
            )}
          </h3>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={(value) => formatCurrency(value)} />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />

            {filterByCategory && selectedCategories.length > 0 ? (
              selectedCategories.map((categoryId, index) => {
                const category = availableCategories.find(c => c.id === categoryId);
                const categoryName = category ? category.name : categoryId.split('.')[1];

                return (
                  <Line
                    key={categoryId}
                    type="monotone"
                    dataKey={categoryName}
                    name={categoryName}
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth={2}
                    activeDot={{ r: 8 }}
                  />
                );
              })
            ) : (
              <Line
                type="monotone"
                dataKey="value"
                name={metricName}
                stroke="#3273dc"
                strokeWidth={2}
                activeDot={{ r: 8 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // Função renderBarChart
  const renderBarChart = () => {
    const data = prepareChartData();
     const metricName = filterByCategory && selectedCategories.length > 0
      ? "Categorias Selecionadas"
      : (metrics.find(m => m.id === selectedMetric)?.name || selectedMetric);

    const chartTitle = filterByCategory && selectedCategories.length > 0
      ? "Comparação de Categorias por Período"
      : `Comparação de ${metricName} por Período`;

    const barCategoryGapValue = '20%';

    return (
      <div className="chart-container enhanced-chart">
        <div className="chart-header">
          <h3>
            {chartTitle}
            {filterByCategory && selectedCategories.length > 0 && (
              <span className="selected-categories-count">{selectedCategories.length}</span>
            )}
          </h3>
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
            barGap={8}
            barCategoryGap={barCategoryGapValue}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#eaeaea"
            />
            <XAxis
              dataKey="name"
              tick={{ fill: '#555', fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e0e0e0' }}
              dy={15}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tickFormatter={(value) => formatCurrency(value)}
              tick={{ fill: '#555', fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e0e0e0' }}
              dx={-10}
            />
            <Tooltip
              formatter={(value) => formatCurrency(value)}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: 'none',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                padding: '10px 14px',
                fontSize: '13px'
              }}
            />
            <Legend
              verticalAlign="top"
              height={36}
              iconType="circle"
              iconSize={8}
            />

            {filterByCategory && selectedCategories.length > 0 ? (
              selectedCategories.map((categoryId, index) => {
                const category = availableCategories.find(c => c.id === categoryId);
                const categoryName = category ? category.name : categoryId.split('.')[1];

                return (
                  <Bar
                    key={categoryId}
                    dataKey={categoryName}
                    name={categoryName}
                    fill={COLORS[index % COLORS.length]}
                    maxBarSize={60}
                    radius={[4, 4, 0, 0]}
                  />
                );
              })
            ) : (
              <Bar
                dataKey="value"
                name={metricName}
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
                fill="#3273dc"
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  // JSX do componente
  return (
    <MainLayout userName={user?.displayName || "Usuário"}>
      <div className="charts-container">
        <div className="period-selector-container">
          <div className="period-selector-header">
            <h2>
              Gráficos Financeiros - {activeProfileInfo.name}
              {activeProfileInfo.type === 'business' && activeProfileInfo.profileId && 
               activeProfileInfo.profileId !== 'business' && (
                <span className="profile-indicator"> (Perfil: {activeProfileInfo.profileId})</span>
              )}
            </h2>
             {!loading && financialData.length > 0 && (
                <div className="periods-to-show-dropdown">
                  <label htmlFor="periods-to-show-select">Mostrar:</label>
                  <select
                    id="periods-to-show-select"
                    value={periodsToShow}
                    onChange={handlePeriodsToShowChange}
                    disabled={loadingInitial || loadingBackground}
                    className="period-select"
                  >
                    {periodOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
             )}
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {(loading || loadingInitial) ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Carregando dados iniciais dos períodos...</p>
          </div>
        ) : financialData.length === 0 ? (
          <div className="no-data-message">
            <p>Nenhum dado financeiro disponível para o perfil {activeProfileInfo.name}. Importe arquivos OFX ou adicione entradas manuais para visualizar gráficos.</p>
          </div>
        ) : (
          <>
            {loadingBackground && (
              <div className="background-loading-indicator">
                <span className="background-loading-spinner"></span>
                Carregando dados mais antigos...
              </div>
            )}

            <div className="charts-controls">
              <div className="chart-type-selector">
                 <label>Tipo de Gráfico:</label>
                 <div className="chart-buttons">
                   <button
                     className={`chart-button ${activeChart === 'bar' ? 'active' : ''}`}
                     onClick={() => setActiveChart('bar')}
                   >
                     <span className="chart-icon">📊</span> Barras
                   </button>
                   <button
                     className={`chart-button ${activeChart === 'line' ? 'active' : ''}`}
                     onClick={() => setActiveChart('line')}
                   >
                     <span className="chart-icon">📈</span> Linha
                   </button>
                 </div>
              </div>

              <div className="filter-controls">
                 <div className="filter-toggle">
                   <label>
                     <input
                       type="checkbox"
                       checked={filterByCategory}
                       onChange={() => {
                         setFilterByCategory(!filterByCategory);
                         if (filterByCategory) setSelectedCategories([]);
                       }}
                     />
                     Filtrar por categorias específicas
                     {filterByCategory && (
                       <span className="active-filter-indicator">Filtro ativo</span>
                     )}
                   </label>
                 </div>

                 {filterByCategory && (
                   <div className="improved-category-selector">
                     <div className="category-selector-header">
                       <label>Selecione as categorias:</label>
                       {selectedCategories.length > 0 && (
                         <button
                           className="clear-selection-button"
                           onClick={() => setSelectedCategories([])}
                         >
                           Limpar seleção
                         </button>
                       )}
                     </div>

                     <div className="category-grid">
                       {availableCategories.map(category => (
                         <div
                           key={category.id}
                           className={`category-card ${selectedCategories.includes(category.id) ? 'selected' : ''}`}
                           onClick={() => handleCategorySelection(category.id)}
                           title={`Grupo: ${category.group}`}
                         >
                           <div className="category-card-content">
                             <div className="category-checkbox">
                               <input
                                 type="checkbox"
                                 checked={selectedCategories.includes(category.id)}
                                 readOnly
                               />
                             </div>
                             <div className="category-info">
                               <div className="category-name">{category.name}</div>
                               <div className="category-badge">{
                                 activeAccountType === 'personal' 
                                   ? (category.group.includes('💸') ? 'Receita' : 
                                      category.group.includes('🧾') ? 'Desp. Fixa' : 
                                      category.group.includes('🍞') ? 'Desp. Var.' : 
                                      category.group.includes('💳') ? 'Pagamento' : 'Outro')
                                   : (category.group.includes('CMV') ? 'CMV' : 
                                      category.group.includes('CSP') ? 'CSP' :
                                      category.group.includes('DESPESAS OP') ? 'Desp. Op.' :
                                      category.group.includes('DESPESAS') ? 'Despesa' : 'Outro')
                               }</div>
                             </div>
                           </div>
                         </div>
                       ))}
                     </div>

                     {selectedCategories.length > 0 && (
                       <div className="selected-categories-summary">
                         {selectedCategories.length} categorias selecionadas
                       </div>
                     )}
                   </div>
                 )}
              </div>

              {!filterByCategory && (
                 <div className="metric-selector">
                   <label>Métrica:</label>
                   <select
                     value={selectedMetric}
                     onChange={(e) => setSelectedMetric(e.target.value)}
                     className="period-select"
                   >
                     {metrics.map(metric => (
                       <option key={metric.id} value={metric.id}>{metric.name}</option>
                     ))}
                   </select>
                 </div>
              )}
            </div>

            <div className="charts-content">
              {prepareChartData().length > 0 ? (
                 <>
                   {activeChart === 'bar' && renderBarChart()}
                   {activeChart === 'line' && renderLineChart()}
                 </>
               ) : (
                 <div className="no-data-message" style={{marginTop: '20px'}}>
                   <p>Não há dados para exibir com os filtros selecionados.</p>
                 </div>
               )}
            </div>

            {/* Tabela de Resumo */}
            <div className="periods-summary">
               <h3>Resumo dos Períodos Exibidos</h3>
               {prepareSummaryTableData().length > 0 ? (
                  <div className="summary-table-container">
                    <table className="summary-table">
                      <thead>
                        <tr>
                          <th>Período</th>
                          {filterByCategory && selectedCategories.length > 0 ? (
                            selectedCategories.map(categoryId => {
                              const category = availableCategories.find(c => c.id === categoryId);
                              return (
                                <th key={categoryId}>
                                  {category?.name || categoryId.split('.')[1]}
                                </th>
                              );
                            })
                          ) : (
                            selectedMetric === "resultadoFinal" ? (
                              <th>Resultado Final</th>
                            ) : (
                              <>
                                <th>Receita Bruta</th>
                                <th>Custos Diretos</th>
                                <th>Lucro Bruto</th>
                                <th>Resultado Final</th>
                              </>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {prepareSummaryTableData().map((period, index) => (
                          <tr key={index}>
                            <td>{formatPeriod(period.period)}</td>
                            {filterByCategory && selectedCategories.length > 0 ? (
                              selectedCategories.map(categoryId => {
                                const category = availableCategories.find(c => c.id === categoryId);
                                const categoryName = category ? category.name : categoryId.split('.')[1];
                                const value = period[categoryName] || 0;

                                return (
                                  <td key={categoryId} className={value >= 0 ? "amount-positive" : "amount-negative"}>
                                    {formatCurrency(value)}
                                  </td>
                                );
                              })
                            ) : (
                              selectedMetric === "resultadoFinal" ? (
                                <td className={period.resultadoFinal >= 0 ? "amount-positive" : "amount-negative"}>
                                  {formatCurrency(period.resultadoFinal)}
                                </td>
                              ) : (
                                <>
                                  <td className="amount-positive">{formatCurrency(period.receitaBruta)}</td>
                                  <td className="amount-negative">{formatCurrency(period.custosDiretosAbs)}</td>
                                  <td className={period.lucroBruto >= 0 ? "amount-positive" : "amount-negative"}>
                                    {formatCurrency(period.lucroBruto)}
                                  </td>
                                  <td className={period.resultadoFinal >= 0 ? "amount-positive" : "amount-negative"}>
                                    {formatCurrency(period.resultadoFinal)}
                                  </td>
                                </>
                              )
                            )}
                          </tr>
                        ))}

                        {/* Linha de totais */}
                         <tr className="total-row">
                           <td><strong>TOTAL</strong></td>
                           {filterByCategory && selectedCategories.length > 0 ? (
                               selectedCategories.map(categoryId => {
                                   const category = availableCategories.find(c => c.id === categoryId);
                                   const categoryName = category ? category.name : categoryId.split('.')[1];
                                   const totalValue = prepareSummaryTableData().reduce((sum, p) => sum + (p[categoryName] || 0), 0);
                                   return (
                                       <td key={categoryId} className={totalValue >= 0 ? "amount-positive" : "amount-negative"}>
                                           {formatCurrency(totalValue)}
                                       </td>
                                   );
                               })
                           ) : (
                               selectedMetric === "resultadoFinal" ? (
                                 <td className={prepareSummaryTableData().reduce((sum, p) => sum + (p.resultadoFinal || 0), 0) >= 0 ? "amount-positive" : "amount-negative"}>
                                     {formatCurrency(prepareSummaryTableData().reduce((sum, p) => sum + (p.resultadoFinal || 0), 0))}
                                 </td>
                               ) : (
                                 <>
                                     <td className="amount-positive">
                                         {formatCurrency(prepareSummaryTableData().reduce((sum, p) => sum + (p.receitaBruta || 0), 0))}
                                     </td>
                                     <td className="amount-negative">
                                         {formatCurrency(prepareSummaryTableData().reduce((sum, p) => sum + (p.custosDiretosAbs || 0), 0))}
                                     </td>
                                     <td className={prepareSummaryTableData().reduce((sum, p) => sum + (p.lucroBruto || 0), 0) >= 0 ? "amount-positive" : "amount-negative"}>
                                         {formatCurrency(prepareSummaryTableData().reduce((sum, p) => sum + (p.lucroBruto || 0), 0))}
                                     </td>
                                     <td className={prepareSummaryTableData().reduce((sum, p) => sum + (p.resultadoFinal || 0), 0) >= 0 ? "amount-positive" : "amount-negative"}>
                                         {formatCurrency(prepareSummaryTableData().reduce((sum, p) => sum + (p.resultadoFinal || 0), 0))}
                                     </td>
                                 </>
                               )
                           )}
                       </tr>
                      </tbody>
                    </table>
                  </div>
               ) : (
                 <div className="no-data-message" style={{marginTop: '20px'}}>
                    <p>Não há dados para exibir na tabela com os filtros selecionados.</p>
                 </div>
               )}
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default Charts;